<template>
  <v-form
    ref="form"
    :class="{ disabled: readOnly, 'px-5': readOnly }"
    v-if="org && org.settings"
    id="org-settings-form"
    @keyup.enter="submit">
    <v-row>
      <v-col lg="10" md="12">
        <div class="mb-10">
          <h3>Overview</h3>
          <p>
            <small>
              {{ $t('org.pages.settings.messages.detailsAndIdentificationOfYourOrganization') }}
            </small>
          </p>
          <v-list width="100%" elevation="2" class="py-0">
            <v-list-item>
              <v-list-item-title>
                <strong class="col-2">{{ $t('org.pages.settings.labels.orgName') }}:</strong>
                {{ org.name }}
              </v-list-item-title>
              <template #append>
                <audit-log-entity-icon-button
                  :item="org"
                  size="small"
                  v-if="!readOnly"
                  timezone="UTC"
                  :entity-type="novaCore.DatabaseEntities.Org"></audit-log-entity-icon-button>
                <icon-tooltip-button
                  v-if="!readOnly"
                  :tooltip="$t('org.pages.settings.tooltips.editOrgName')"
                  size="small"
                  @click="showOrgDialog = true"
                  :disabled="readOnly"
                  icon="mdi-pencil"></icon-tooltip-button>
              </template>
            </v-list-item>
            <v-list-item variant="elevated">
              <v-list-item-title>
                <strong class="col-2">
                  {{ $t('org.pages.settings.labels.orgId') }}
                  <help-icon-tooltip>
                    {{ $t('org.pages.settings.tooltips.thisOrgsUniqueId') }}
                  </help-icon-tooltip>
                  :
                </strong>
                {{ org.id }}
              </v-list-item-title>
              <template #append>
                <v-icon class="mr-2 opacity-100" color="secondary" @click="copyOrgId" size="small">
                  mdi-content-copy
                </v-icon>
              </template>
            </v-list-item>
          </v-list>
        </div>
        <edit-org-dialog
          external-activator
          :show-dialog="showOrgDialog"
          @close="updateOrgName"></edit-org-dialog>
        <div class="mb-10">
          <h3>Settings</h3>
          <p>
            <small>
              {{
                $t(
                  'org.pages.settings.messages.changesWillBeAppliedToAllWarehousesAcrossYourOrganization'
                )
              }}.
            </small>
          </p>
          <settings-expansion-panels
            :read-only="readOnly"
            :entity-key="novaCore.DatabaseEntities.Org"
            :entity-settings="this.org.settings"
            @update-settings="updateSettings">
            <template #prepended-panel>
              <v-expansion-panel>
                <v-expansion-panel-title :class="{ disabled: readOnly }" ripple>
                  {{ $t('org.pages.settings.headers.appointmentTags') }}
                </v-expansion-panel-title>
                <v-divider></v-divider>
                <v-expansion-panel-text :class="{ disabled: readOnly }">
                  <custom-tags-form :read-only="readOnly"></custom-tags-form>
                </v-expansion-panel-text>
              </v-expansion-panel>
            </template>
          </settings-expansion-panels>
        </div>
        <action-group
          class="ml-2 mb-6"
          confirm-icon="upload"
          :confirm-label="$t('org.pages.settings.buttons.saveOrgSettings')"
          :disable-confirm="$isOrgReadOnly"
          :confirm-button-tooltip="$isOrgReadOnly ? novaCore.getRoleActionError() : ''"
          :hide-cancel="true"
          @confirm="submit"></action-group>
      </v-col>
    </v-row>
  </v-form>
</template>

<script>
export default {
  props: {
    readOnly: {
      type: Boolean,
      required: false,
      default: false
    }
  },
  data() {
    return {
      org: null,
      showOrgDialog: false
    };
  },
  methods: {
    updateOrgName() {
      this.showOrgDialog = false;
      this.org.name = this.$org.name;
    },
    copyOrgId() {
      if (this.org?.id) {
        navigator.clipboard.writeText(this.org.id);
        this.notify(this.$t('global.phrases.copiedToClipboard'));
      }
    },
    /**
     * Submit Form
     * @public
     */
    async submit() {
      let settings = {};

      // Remove property from settings if no value
      if (this.org?.settings) {
        settings = { ...this.org.settings };
        Object.entries(settings).map(([key, value]) => {
          if (!value && value !== 0 && value !== false) {
            delete settings[key];
          } else if (key === 'muteAppointmentNotifications') {
            // Invert because we are displaying as "Notifications ENABLED" instead of "DISABLED"
            settings[key] = !settings[key];
          }
        });
        if (settings.appointmentStartTimeMinutes !== 'custom') {
          delete settings.customAppointmentStartTimes;
        }
      }

      try {
        const updatedOrg = await this.$store.dispatch('Orgs/updateOrg', {
          ...this.org,
          settings
        });

        if (updatedOrg) {
          this.mixpanel.track(this.mixpanel.events.MODULE.ORG.SETTINGS, {
            'Org Name': this.org.name,
            'Org ID': this.org.id
          });
          await this.$store.dispatch('Orgs/getOrg', this.org.id);
        }
      } catch {
        // No op, axios plugin handles displaying the error
      }
    },
    // Sets default values on form fields, unless the value type is a string
    setDefaultValuesOnForm() {
      this.novaCore.getDefaultOrgSettings(this.org.settings);

      // Invert because we are displaying as "Notifications ENABLED" instead of "DISABLED"
      if (typeof this.org.settings.muteAppointmentNotifications === 'undefined') {
        this.org.settings.muteAppointmentNotifications =
          !this.org.settings.muteAppointmentNotifications;
      }
    },
    updateSettings(settings) {
      this.org.settings = settings;
    }
  },
  async mounted() {
    if (this.$me.orgId) {
      this.org = await this.$store.dispatch('Orgs/getOrg', this.$me.orgId);
      this.org = this.novaCore.deepClone(this.org);

      this.org.settings = this.org.settings || {};

      if (typeof this.org.settings.muteAppointmentNotifications === 'boolean') {
        // Invert because we are displaying as "Notifications ENABLED" instead of "DISABLED"
        this.org.settings.muteAppointmentNotifications =
          !this.org.settings.muteAppointmentNotifications;
      }

      this.setDefaultValuesOnForm();
    }
  }
};
</script>

<style lang="scss">
#org-settings-form {
  .v-text-field__details {
    padding-left: 0 !important;
  }
}
</style>
